import { useIsFocused } from '@react-navigation/native';
import * as Sentry from '@sentry/react-native';
import dayjs from 'dayjs';
import { Formik } from 'formik';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import {
  Animated,
  Dimensions,
  Easing,
  FlatList,
  TouchableOpacity,
} from 'react-native';

import { TeamsModal } from '@components/FormModals/TeamsModal';
import { ContactType } from '@components/Invite/Contact';
import { CompleteProfilePhoneBanner } from '@components/Profile/CompleteProfilePhoneBanner';
import ProjectCard from '@components/ProjectCard/ProjectCard.web';
import ProjectList from '@components/ProjectList/ProjectList.web';
import { Box } from '@components/Restyle/index';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import { WebIcon } from '@components/shared/Icon/WebIcon';
import ShareModal from '@components/shared/ShareModal';
import { Skeleton } from '@components/shared/Skeleton';
import {
  ProjectCardSkeleton,
  ProjectListRowSkeleton,
  SkeletonList,
} from '@components/shared/skeletons';
import { MultiTags } from '@components/shared/Tags/MultiTags.web';
import Text from '@components/Text/Text';
import { useWebDrawer } from '@components/Web/Drawer/WebDrawerContext';
import { PopupProjectButtonWeb } from '@components/Web/PopupProjectButton.web';
import { PopupProjectsFiltersButtonWeb } from '@components/Web/PopupProjectsFiltersButton.web';
import { WelcomeModalScreen } from '@components/Web/WelcomeModalScreen.web';
import {
  ListTeamsDocument,
  ListTeamsQuery,
  LocalFile,
  ProjectSortOption,
  useCreateTeamMutation,
  useProjectListNodeUpdatedSubscription,
  useUpdateOnboardingStateMutation,
} from '@graphql/generated';
import useActiveChat from '@hooks/useActiveChat';
import useFileProcessor from '@hooks/useFileProcessor';
import { useListProjectsFromQuery } from '@hooks/useListProjectsFromQuery';
import useMe from '@hooks/useMe';
import { useOnboardingActive } from '@hooks/useOnboardingActive';
import { useWebDrawerNavigator } from '@hooks/useWebDrawerNavigator';
import { FormValues } from '@navigation/teams/create-team-stack';
import { useAppNavigation } from '@navigation/useAppNavigation';
import { createTeamSchema } from '@schemas/createTeamSchema';
import theme, { getScaledSize, SCALED_DIMENSIONS } from '@themes/theme';
import { patchProjectListCache } from '@utils/cache/patchProjectListCache';
import { readPendingTeamSignup } from '@utils/teams/pendingTeamSignup';
import { uploadTeamLogo } from '@utils/teams/uploadTeamLogo';

import type { Project, Team } from '@graphql/generated';

const PROJECT_LIMIT = 4;
const COMPLETE_PROFILE_BANNER_STORAGE_KEY =
  'complete_profile_phone_banner_seen';

// TA-10273: 3 ProjectCardSkeleton placeholders for the "Recent" strip —
// shared between the cold-load (no cache at all) and warm-load (Recent
// already loaded, main list still fetching) skeleton branches below so
// both look identical.
const RecentProjectsSkeletonRow: React.FC = () => (
  <Box flexDirection='row'>
    {[0, 1, 2].map((index) => (
      <Box
        key={index}
        marginRight={Dimensions.get('window').width <= 1280 ? 'xs' : 'm'}>
        <ProjectCardSkeleton />
      </Box>
    ))}
  </Box>
);

// TA-10273: stand-in for the Active/Archived toggle + Owner/Team/Member
// filter buttons — keeps the toolbar's silhouette in place (instead of
// disappearing or staying clickable) while the project list is still
// loading, so a user can't open a filter dropdown before the list it
// filters is ready. Matches the real toolbar's split: 1 button on the
// left (Active/Archived Projects), 5 on the right (Owner, Team, Member,
// grid/list toggle, Create Project).
const FilterToolbarSkeleton: React.FC = () => (
  <Box
    testID='filter-toolbar-skeleton'
    width='100%'
    flexDirection='row'
    alignItems='center'
    justifyContent='space-between'
    marginBottom='m'>
    <Skeleton variant='rect' width={140} height={32} borderRadius='s' />
    <Box
      flexDirection='row'
      alignItems='center'
      style={{ gap: theme.spacing.m }}>
      <Skeleton variant='rect' width={90} height={32} borderRadius='s' />
      <Skeleton variant='rect' width={90} height={32} borderRadius='s' />
      <Skeleton variant='rect' width={90} height={32} borderRadius='s' />
      <Skeleton variant='rect' width={32} height={32} borderRadius='xxs' />
      <Skeleton variant='rect' width={140} height={32} borderRadius='xs' />
    </Box>
  </Box>
);

type TutorialTag = {
  title: string;
  textColor: string;
  backgroundColor: string;
};

export type TutorialItem = {
  title: string;
  tag?: TutorialTag;
  description: string;
  buttonText?: string;
  onButtonPress?: () => void;
  currentStep: number;
  img: string;
};

export const AllProjects: React.FC = () => {
  const { t } = useTranslation('models');
  const navigation = useAppNavigation();
  const { isOpenChat } = useActiveChat();

  const isFocused = useIsFocused();

  const {
    chatListOpen,
    setIsCreateProjectOpen,
    setIsEditProfileOpen,
    setEditProjectUUId,
    setIsTaskWebPanelOpen,
    setIdTaskDetailOpen,
    setIdProjectOfTaskDetailOpen,
    setIsTaskWebPanelMaximize,
  } = useWebDrawer();
  const { me } = useMe();
  const onboardingActive = useOnboardingActive();

  const [showBoardingWelcomeModal, setShowBoardingWelcomeModal] =
    useState(false);
  const [archivesOnly, setArchivesOnly] = useState(false);
  const { selectedProject, setSelectedProject } = useWebDrawerNavigator();
  useEffect(() => {
    selectedProject && setSelectedProject(undefined);
  }, []);
  const [selectedTeams, setSeletectedTeams] = useState<
    Pick<Team, 'id' | 'name' | 'avatar' | 'usersCount'>[]
  >([]);
  const [selectedOwners, setSelectedOwners] = useState<ContactType[]>([]);
  const [selectedMembers, setSelectedMembers] = useState<ContactType[]>([]);

  const [barWidth, setBarWidth] = useState(0);
  const [filterTagAreaWidth, setFilterTagAreaWidth] = useState(0);
  const [filterOwnerTagWidth, setFilterOwnerTagWidth] = useState(0);
  const [filterTeamTagWidth, setFilterTeamTagWidth] = useState(0);
  const [filterMemberTagWidth, setFilterMemberTagWidth] = useState(0);
  const [clearButtonWidth, setClearButtonWidth] = useState(30);
  const [bodyWidth, setBodyWidth] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [search] = useState('');
  const [showGrid, setShowGrid] = useState(false);

  const [hasSeenCompleteProfileBanner, setHasSeenCompleteProfileBanner] =
    useState<boolean | null>(null);
  const [isCompleteProfileBannerVisible, setIsCompleteProfileBannerVisible] =
    useState(false);
  const marginRight =
    Dimensions.get('window').width <= 1280 ? theme.spacing.xs : theme.spacing.m;
  const cardWidth =
    (bodyWidth - marginRight * (PROJECT_LIMIT - 1)) / PROJECT_LIMIT;
  const [modalVisible, setModalVisible] = useState(false);
  const [showCreateTeamModal, setShowCreateTeamModal] = useState(false);
  const {
    setUpgradeTeamPlanOpen,
    setIsTeamPersonal,
    setIdTeamOpen,
    setUUIdTeamOpen,
    setTeamValues,
    setIsNewTeam,
    upgradeConfirmModalOpen,
    isUpgradeTeamPlanOpen,
    subscriptionCreatedModalOpen,
  } = useWebDrawer();

  const recentProjectsRef = useRef<FlatList>(null);
  const cachedRecentProjectsRef = useRef<Project[]>([]);

  const [updateOnboardingState] = useUpdateOnboardingStateMutation();
  const [createTeam] = useCreateTeamMutation({
    update(cache, { data }) {
      const newTeam = data?.createTeam;
      if (!newTeam?.id) return;
      for (const variables of [
        {},
        { includePending: false },
        { includePending: true },
      ]) {
        const existing = cache.readQuery<ListTeamsQuery>({
          query: ListTeamsDocument,
          variables,
        });
        if (!existing) continue;
        cache.writeQuery<ListTeamsQuery>({
          query: ListTeamsDocument,
          variables,
          data: {
            listTeams: [
              ...existing.listTeams,
              {
                ...(newTeam as ListTeamsQuery['listTeams'][0]),
                hasProjects: false,
              },
            ],
          },
        });
      }
    },
  });
  const { processFiles } = useFileProcessor();
  useEffect(() => {
    const type = globalThis.sessionStorage.getItem('link_type');
    const id = globalThis.sessionStorage.getItem('task_or_project_id');
    const invitationToken =
      globalThis.sessionStorage.getItem('invitation_token');
    // When an invitation_token is present the AppStack invite flow owns this
    // navigation — it calls getPublicProjectForInvite, creates the MemberAddition
    // request if needed, then navigates. Don't consume or clear the keys here.
    if (invitationToken) return;
    const UUID_REGEX =
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
    if (type === 'project') {
      if (id && UUID_REGEX.test(id)) {
        showBoardingWelcomeModal && setShowBoardingWelcomeModal(false);
        return;
      }
    } else if (type === 'task') {
      if (id) {
        showBoardingWelcomeModal && setShowBoardingWelcomeModal(false);
        return;
      }
    }
    // TA-10886: the post-registration team checkout owns the screen while it
    // runs — don't stack the onboarding tutorial on top of it, and don't burn
    // welcomeModalSeen while it is hidden. subscriptionCreatedModalOpen is
    // part of the chain: payment success closes the checkout panel and opens
    // the success modal, and without it the tutorial would land on top of
    // that instead — the same two-modals symptom, one step later. This effect
    // re-runs once the chain finishes, so the tutorial still appears after.
    if (
      upgradeConfirmModalOpen ||
      isUpgradeTeamPlanOpen ||
      subscriptionCreatedModalOpen ||
      readPendingTeamSignup()
    ) {
      showBoardingWelcomeModal && setShowBoardingWelcomeModal(false);
      return;
    }
    if (
      isFocused &&
      onboardingActive &&
      me?.onboardingState?.welcomeModalSeen === false
    ) {
      if (window.sessionStorage.getItem('skip_welcome_modal') === 'true')
        return;
      setShowBoardingWelcomeModal(true);
      updateOnboardingState({
        variables: {
          attributes: {
            welcomeModalSeen: true,
          },
        },
        update: (cache) => {
          if (!me?.id) return;
          cache.modify({
            id: cache.identify({ __typename: 'User', id: me.id }),
            fields: {
              onboardingState: (existing) => ({
                ...existing,
                welcomeModalSeen: true,
              }),
            },
          });
        },
        onCompleted: () => {
          window.sessionStorage.removeItem('skip_welcome_modal');
        },
      });
    } else if (window.sessionStorage.getItem('skip_welcome_modal') === 'true') {
      setShowBoardingWelcomeModal(false);
    } else {
      window.sessionStorage.removeItem('skip_welcome_modal');
    }
  }, [
    onboardingActive,
    me?.onboardingState?.welcomeModalSeen,
    isFocused,
    window.sessionStorage,
    upgradeConfirmModalOpen,
    isUpgradeTeamPlanOpen,
    subscriptionCreatedModalOpen,
  ]);

  // for welcome modal
  const modalProgress = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (!showBoardingWelcomeModal) return;

    modalProgress.setValue(0);
    Animated.timing(modalProgress, {
      toValue: 1,
      duration: 280,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start();
  }, [modalProgress, showBoardingWelcomeModal]);

  const modalTranslateY = modalProgress.interpolate({
    inputRange: [0, 1],
    outputRange: [18, 0],
  });

  const modalScale = modalProgress.interpolate({
    inputRange: [0, 1],
    outputRange: [0.96, 1],
  });

  const windowWidth = Dimensions.get('window').width;
  const { setMainPanelWidth } = useWebDrawer();

  const offsetPopButton1: Partial<any> = {
    position: 'right',
    offset: [0, 6],
  };

  const offsetPopButton: Partial<any> = {
    position: 'left',
    offset: [0, 6],
  };
  const spaceBetweenTags = 6;
  const isCompleteProfileBannerEligible =
    !me?.phoneNumber?.trim() &&
    !!me?.createdAt &&
    dayjs().diff(dayjs(me.createdAt), 'day') >= 7;
  const shouldShowCompleteProfileBanner = isCompleteProfileBannerVisible;

  useEffect(() => {
    if (typeof globalThis.window === 'undefined') return;

    const hasSeen =
      globalThis.localStorage.getItem(COMPLETE_PROFILE_BANNER_STORAGE_KEY) ===
      '1';
    setHasSeenCompleteProfileBanner(hasSeen);
  }, []);

  useEffect(() => {
    if (hasSeenCompleteProfileBanner === null) return;

    if (!isCompleteProfileBannerEligible) {
      setIsCompleteProfileBannerVisible(false);
      return;
    }

    if (isCompleteProfileBannerVisible) return;

    if (hasSeenCompleteProfileBanner) {
      setIsCompleteProfileBannerVisible(false);
      return;
    }

    setIsCompleteProfileBannerVisible(true);

    if (typeof globalThis.window !== 'undefined') {
      globalThis.localStorage.setItem(COMPLETE_PROFILE_BANNER_STORAGE_KEY, '1');
    }
    setHasSeenCompleteProfileBanner(true);
  }, [
    hasSeenCompleteProfileBanner,
    isCompleteProfileBannerEligible,
    isCompleteProfileBannerVisible,
  ]);

  const {
    projects,
    recentProjects,
    loading,
    error,
    refreshControl,
    fetchFromCursor,
    refreshing,
    refetch,
  } = useListProjectsFromQuery({
    sortBy: ProjectSortOption.NameAsc,
    archivesOnly: archivesOnly,
    ownerIds: [...selectedOwners.map((t) => t.id)],
    memberIds: [...selectedMembers.map((t) => t.id)],
    teamIds: [...selectedTeams.map((t) => t.id)],
    recentFirst: PROJECT_LIMIT,
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    if (!loading && !error && recentProjects.length > 0) {
      cachedRecentProjectsRef.current = recentProjects;
    }
  }, [loading, error, recentProjects]);

  // Latches once the project list has finished loading a single time, so a
  // later Owner/Team/Member filter change (which briefly resets loading with
  // no cached data for the new, uncached variable combo) doesn't re-trigger
  // the skeleton and remount the CTA row, closing its open filter dropdown
  // (TA-10949 #27).
  const hasLoadedProjectsOnceRef = useRef(false);
  useEffect(() => {
    if (!loading) {
      hasLoadedProjectsOnceRef.current = true;
    }
  }, [loading]);

  const isFocusedRef = useRef(isFocused);
  useEffect(() => {
    isFocusedRef.current = isFocused;
  }, [isFocused]);

  useProjectListNodeUpdatedSubscription({
    onData: ({ data, client }) => {
      if (!isFocusedRef.current) return;
      const project = data.data?.projectUpdated;
      if (!project) return;
      patchProjectListCache(client, project);
    },
  });

  useEffect(() => {
    const type = globalThis.sessionStorage.getItem('link_type');
    const id = globalThis.sessionStorage.getItem('task_or_project_id');
    const invitationToken =
      globalThis.sessionStorage.getItem('invitation_token');
    // When an invitation_token is present the AppStack invite flow owns this
    // navigation — it calls getPublicProjectForInvite, creates the MemberAddition
    // request if needed, then navigates. Don't consume or clear the keys here.
    if (invitationToken) return;
    const UUID_REGEX =
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
    if (type === 'project') {
      if (id && UUID_REGEX.test(id)) {
        setShowBoardingWelcomeModal(false);
        navigation.navigateToProject({ uuid: id, id: '' });
      } else if (id) {
        Sentry.captureMessage(
          `Project navigation received non-UUID id: ${id}`,
          'warning'
        );
      }
    } else if (type === 'task') {
      if (id) {
        setShowBoardingWelcomeModal(false);
        navigation.navigateToTask({ id: id, projectId: '' }, false, true);
      } else {
        Sentry.captureMessage(
          'Task deep-link received with missing id',
          'warning'
        );
      }
    }
    globalThis.sessionStorage.setItem('link_type', '');
    globalThis.sessionStorage.setItem('task_or_project_id', '');
  }, []);

  // Open immediately, like New Task. The old 1s delay (1–3 slots left) waited
  // for a project-remaining snackbar that was removed in #3609, so it only
  // postponed the panel (TA-10891 QA).
  const createProject = () => {
    setEditProjectUUId('');
    setIsCreateProjectOpen(true);
  };

  const initialValues: FormValues = {
    name: '',
    skillIds: [],
    logo: null,
    location: null,
  };

  const submitFormTeam = async (
    values: FormValues,
    setSubmitting: (a: boolean) => void
  ) => {
    setSubmitting(true);

    try {
      const { name, location, skillIds, logo } = values;
      let createTeamResult;

      // Only proceed if name is provided as it is required.
      if (name) {
        const avatarBlobId = await uploadTeamLogo(
          logo as LocalFile | null,
          processFiles
        );
        const attributes = {
          name,
          skillIds: skillIds ?? [],
          address: location?.address,
          latitude: location?.latitude,
          longitude: location?.longitude,
          avatarBlobId,
        };

        createTeamResult = await createTeam({ variables: { attributes } });
      }

      // After successful team creation, handle UI updates
      setIsTaskWebPanelOpen(false);
      setIsTaskWebPanelMaximize(false);
      setIdTaskDetailOpen('');
      setIdProjectOfTaskDetailOpen('');
      setIsCreateProjectOpen(false);
      setEditProjectUUId('');
      setUpgradeTeamPlanOpen(true);

      if (createTeamResult?.data?.createTeam) {
        setIdTeamOpen(createTeamResult.data.createTeam.id);
        setUUIdTeamOpen(createTeamResult.data.createTeam.uuid);
        setIsNewTeam(true);
      } else {
        setIdTeamOpen('');
        setUUIdTeamOpen('');
        setIsNewTeam(false);
      }

      setIsTeamPersonal(false);
      setTeamValues(values);
    } catch (error) {
      Sentry.captureException(
        new Error('Form submission failed: ' + JSON.stringify(error))
      );
    } finally {
      setSubmitting(false);
    }
  };

  const hasActiveFilters =
    selectedOwners.length > 0 ||
    selectedTeams.length > 0 ||
    selectedMembers.length > 0;

  // These layout predicates were recomputed on every render (6 onLayout state
  // setters cascade re-renders). Memoize each on the widths/selections it
  // actually reads so opening or changing a filter doesn't re-run all four on
  // unrelated renders. Declared before the early returns below so hook order
  // stays stable across renders (rules-of-hooks). (TA-10044)
  const isOwnerInFirstLine = useMemo(() => {
    if (windowWidth <= 1440) return false;
    const x =
      selectedMembers.length > 0 || selectedTeams.length > 0
        ? 0
        : clearButtonWidth + theme.spacing.xs;
    return (
      selectedOwners.length > 0 && filterOwnerTagWidth + x <= filterTagAreaWidth
    );
  }, [
    windowWidth,
    selectedMembers.length,
    selectedTeams.length,
    selectedOwners.length,
    clearButtonWidth,
    filterOwnerTagWidth,
    filterTagAreaWidth,
  ]);

  const isTeamInFirstLine = useMemo(() => {
    if (windowWidth <= 1440) return false;
    const x =
      selectedTeams.length > 0 ? 0 : clearButtonWidth + theme.spacing.xs;
    return (
      selectedTeams.length > 0 &&
      (selectedOwners.length > 0 ? filterOwnerTagWidth + spaceBetweenTags : 0) +
        filterTeamTagWidth +
        x <=
        filterTagAreaWidth
    );
  }, [
    windowWidth,
    selectedTeams.length,
    selectedOwners.length,
    clearButtonWidth,
    filterOwnerTagWidth,
    filterTeamTagWidth,
    filterTagAreaWidth,
    spaceBetweenTags,
  ]);

  const isMemberInFirstLine = useMemo(() => {
    if (windowWidth <= 1440) return false;
    const x = clearButtonWidth + theme.spacing.xs;
    return (
      selectedMembers.length > 0 &&
      (selectedOwners.length > 0 ? filterOwnerTagWidth + spaceBetweenTags : 0) +
        (selectedTeams.length > 0 ? filterTeamTagWidth + spaceBetweenTags : 0) +
        x +
        filterMemberTagWidth <=
        filterTagAreaWidth
    );
  }, [
    windowWidth,
    selectedMembers.length,
    selectedOwners.length,
    selectedTeams.length,
    clearButtonWidth,
    filterOwnerTagWidth,
    filterTeamTagWidth,
    filterMemberTagWidth,
    filterTagAreaWidth,
    spaceBetweenTags,
  ]);

  const isClearButtonInFirstLine = useMemo(() => {
    if (windowWidth <= 1440) return false;
    return (
      (selectedOwners.length > 0 ||
        selectedTeams.length > 0 ||
        selectedMembers.length > 0) &&
      (selectedOwners.length > 0 ? filterOwnerTagWidth + spaceBetweenTags : 0) +
        (selectedTeams.length > 0 ? filterTeamTagWidth + spaceBetweenTags : 0) +
        (selectedMembers.length > 0
          ? filterMemberTagWidth + spaceBetweenTags
          : 0) +
        clearButtonWidth +
        2 <=
        filterTagAreaWidth
    );
  }, [
    windowWidth,
    selectedOwners.length,
    selectedTeams.length,
    selectedMembers.length,
    filterOwnerTagWidth,
    filterTeamTagWidth,
    filterMemberTagWidth,
    clearButtonWidth,
    filterTagAreaWidth,
    spaceBetweenTags,
  ]);

  if (error && !loading && projects?.length < 1 && recentProjects.length < 1) {
    return (
      <Box flex={1} justifyContent='center' alignItems='center' padding='l'>
        <Text variant='headerLabel' textAlign='center' marginBottom='m'>
          {t('models:projects.errorState.title')}
        </Text>
        <Text variant='bodySecondary' textAlign='center' marginBottom='l'>
          {t('models:projects.errorState.message')}
        </Text>
        <Button
          variant='primary'
          onPress={() => refetch().catch(() => {})}
          accessibilityLabel={t('models:projects.errorState.retry')}>
          {t('models:projects.errorState.retry')}
        </Button>
      </Box>
    );
  }

  if (
    loading &&
    !hasActiveFilters &&
    projects?.length < 1 &&
    recentProjects.length < 1
  ) {
    return (
      <Box flex={1} padding='m'>
        <Text variant='labelEmphasized' mb='m'>
          {t('projects.sections.recent')}
        </Text>
        <Box mb='l'>
          <RecentProjectsSkeletonRow />
        </Box>
        <FilterToolbarSkeleton />
        <SkeletonList row={(index) => <ProjectListRowSkeleton key={index} />} />
      </Box>
    );
  }

  const OwnersTag = () => {
    if (selectedOwners.length === 0) return <></>;
    return (
      <Box
        onLayout={(event) => {
          setFilterOwnerTagWidth(event.nativeEvent.layout.width);
        }}>
        <MultiTags
          prefixText='Owner: '
          key='dateRange'
          labelList={selectedOwners}
          labelVariant='metadata'
          variant='pickerForWeb'
          padding='xxs'
          marginTop='xxs'
          borderRadius='xxs'
          onPressClose={(ownerid) => {
            setSelectedOwners(selectedOwners.filter((c) => c.id != ownerid));
          }}
        />
      </Box>
    );
  };

  const TeamsTag = () => {
    if (selectedTeams.length == 0) return <></>;
    return (
      <Box
        onLayout={(event) => {
          setFilterTeamTagWidth(event.nativeEvent.layout.width);
        }}>
        <MultiTags
          prefixText='Team: '
          key='dateRange'
          labelList={selectedTeams}
          labelVariant='metadata'
          variant='pickerForWeb'
          padding='xxs'
          marginTop='xxs'
          borderRadius='xxs'
          onPressClose={(teamid) => {
            setSeletectedTeams(selectedTeams.filter((c) => c.id != teamid));
          }}
        />
      </Box>
    );
  };

  const MembersTag = () => {
    if (selectedMembers.length === 0) return <></>;
    return (
      <Box
        onLayout={(event) => {
          setFilterMemberTagWidth(event.nativeEvent.layout.width);
        }}>
        <MultiTags
          prefixText='Member: '
          key='dateRange'
          labelList={selectedMembers}
          labelVariant='metadata'
          variant='pickerForWeb'
          padding='xxs'
          marginTop='xxs'
          borderRadius='xxs'
          onPressClose={(mid) => {
            setSelectedMembers(selectedMembers.filter((c) => c.id != mid));
          }}
        />
      </Box>
    );
  };

  const ClearButton = () => {
    return (
      <Box
        mt='xs'
        onLayout={(e) => {
          setClearButtonWidth(e.nativeEvent.layout.width + 8);
        }}>
        <TouchableOpacity
          onPress={() => {
            setSeletectedTeams([]);
            setSelectedMembers([]);
            setSelectedOwners([]);
          }}>
          <Text variant='metadata' color='grey04'>
            {t('shared:clear')}
          </Text>
        </TouchableOpacity>
      </Box>
    );
  };

  const goToSetSeletectedTeam = (
    item: Pick<Team, 'id' | 'name' | 'avatar' | 'usersCount' | 'personal'>
  ) => {
    const isIn = selectedTeams.some((c) => c.id === item.id);
    const updatedSelected = isIn
      ? selectedTeams.filter((c) => c.id !== item.id)
      : [...selectedTeams, item];
    setSeletectedTeams(updatedSelected);
  };

  const AllProjectSection = () => (
    <Box
      px={Dimensions.get('window').width <= 1280 ? 'm' : 'l'}
      my='m'
      flex={1}
      onLayout={(e) => setMainPanelWidth(e.nativeEvent.layout.width)}
      minWidth={Dimensions.get('window').width <= 1280 ? 639 : 742}>
      {showBoardingWelcomeModal &&
        (() => {
          const modalContent = (
            <Animated.View
              style={{
                position: 'fixed' as any,
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                zIndex: 100000,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: `rgba(0, 0, 0, 0.5)`,
                opacity: modalProgress,
              }}>
              <Animated.View
                style={{
                  transform: [
                    { translateY: modalTranslateY },
                    { scale: modalScale },
                  ],
                }}>
                <WelcomeModalScreen
                  name={me?.firstName ?? 'TaskTag User'}
                  onGetStarted={() => {
                    Animated.timing(modalProgress, {
                      toValue: 0,
                      duration: 300,
                      easing: Easing.in(Easing.cubic),
                      useNativeDriver: true,
                    }).start(() => {
                      setShowBoardingWelcomeModal(false);
                      createProject();
                    });
                  }}
                  style={{
                    maxHeight: 'calc(100vh - 48px)' as any,
                  }}
                />
              </Animated.View>
            </Animated.View>
          );
          return typeof document !== 'undefined'
            ? ReactDOM.createPortal(modalContent, document.body)
            : modalContent;
        })()}

      {error && (
        <Box
          flexDirection='row'
          alignItems='center'
          backgroundColor='alertRedMild'
          paddingHorizontal='m'
          paddingVertical='s'
          mb='s'
          borderRadius='xs'>
          <Box flex={1}>
            <Text variant='labelSmall' color='alertRed'>
              {t('models:projects.errorState.bannerMessage')}
            </Text>
          </Box>
          <TouchableOpacity
            onPress={() => refetch().catch(() => {})}
            accessibilityLabel={t('models:projects.errorState.retry')}>
            <Text
              variant='labelSmall'
              color='alertRed'
              style={{ textDecorationLine: 'underline' }}>
              {t('models:projects.errorState.retry')}
            </Text>
          </TouchableOpacity>
        </Box>
      )}
      {!search && (
        <Box
          mb='l'
          alignItems='flex-start'
          onLayout={(e) => setBodyWidth(e.nativeEvent.layout.width)}>
          <Text variant='labelEmphasized' mb='m'>
            {t('projects.sections.recent')}
          </Text>
          <Box
            style={{
              width: bodyWidth,
              paddingBottom: Dimensions.get('window').width <= 1280 ? 10 : 0,
              minHeight: 124,
            }}>
            {loading && cachedRecentProjectsRef.current.length === 0 ? (
              <RecentProjectsSkeletonRow />
            ) : (
              <FlatList
                horizontal
                ref={recentProjectsRef}
                showsHorizontalScrollIndicator={false}
                indicatorStyle='white'
                data={
                  loading && cachedRecentProjectsRef.current.length > 0
                    ? cachedRecentProjectsRef.current
                    : recentProjects
                }
                renderItem={({ item }) => (
                  <Box
                    key={item.id}
                    width={
                      (chatListOpen || isOpenChat) &&
                      Dimensions.get('window').width <= 1280
                        ? 240
                        : cardWidth
                    }>
                    <ProjectCard
                      isRecent={true}
                      project={item}
                      onClick={() => navigation.navigateToProject(item)}
                    />
                  </Box>
                )}
                ItemSeparatorComponent={() => (
                  <Box
                    marginRight={
                      Dimensions.get('window').width <= 1280 ? 'xs' : 'm'
                    }
                  />
                )}
              />
            )}
          </Box>
          {(chatListOpen || isOpenChat) &&
            Dimensions.get('window').width <= 1280 &&
            currentIndex > 0 && (
              <Icon
                color='black'
                style={{
                  position: 'absolute',
                  left: -11,
                  top: -86,
                  width: 24,
                  height: 24,
                  opacity: 0.5,
                }}
                name='ArrowLeftCircle'
                onPress={() => {
                  recentProjectsRef.current?.scrollToIndex({
                    index: currentIndex > 0 ? currentIndex - 1 : 0,
                    animated: true,
                  });
                  setCurrentIndex(currentIndex - 1);
                }}
              />
            )}
          {(chatListOpen || isOpenChat) &&
            Dimensions.get('window').width <= 1280 &&
            currentIndex < 2 && (
              <Icon
                color='black'
                style={{
                  position: 'absolute',
                  left: Dimensions.get('window').width - 110 - 24 - 11 - 480,
                  top: -86,
                  width: 24,
                  height: 24,
                  opacity: 0.5,
                }}
                name='ArrowRightCircle'
                onPress={() => {
                  recentProjectsRef.current?.scrollToIndex({
                    index: currentIndex < 2 ? currentIndex + 1 : currentIndex,
                    animated: true,
                  });
                  currentIndex < 2 && setCurrentIndex(currentIndex + 1);
                }}
              />
            )}
        </Box>
      )}
      <Box flex={1}>
        {!search &&
          (!hasLoadedProjectsOnceRef.current &&
          loading &&
          projects?.length < 1 ? (
            <FilterToolbarSkeleton />
          ) : (
            <>
              <Box
                flexDirection='row'
                justifyContent='flex-start'
                onLayout={(event) => {
                  setBarWidth(event.nativeEvent.layout.width);
                }}>
                <PopupProjectButtonWeb
                  title={
                    archivesOnly
                      ? t('projects.buttons.archivedProjects')
                      : t('projects.buttons.activeProjects')
                  }
                  boxProps={offsetPopButton1}
                  onPressArchiveProjects={() => {
                    setArchivesOnly(true);
                  }}
                  onPressActiveProjects={() => {
                    setArchivesOnly(false);
                  }}
                />
                <Box mr='xs'></Box>
                <Box
                  style={{
                    flex: 1,
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                  }}
                  onLayout={(e) =>
                    setFilterTagAreaWidth(e.nativeEvent.layout.width)
                  }>
                  {isOwnerInFirstLine && (
                    <Box style={{ marginRight: spaceBetweenTags }}>
                      {OwnersTag()}
                    </Box>
                  )}
                  {isTeamInFirstLine && (
                    <Box style={{ marginRight: spaceBetweenTags }}>
                      {TeamsTag()}
                    </Box>
                  )}
                  {isMemberInFirstLine && (
                    <Box style={{ marginRight: spaceBetweenTags }}>
                      {MembersTag()}
                    </Box>
                  )}
                  {isClearButtonInFirstLine && ClearButton()}
                </Box>

                <Box
                  ml='xs'
                  flexDirection='row'
                  alignItems='center'
                  justifyContent='flex-start'>
                  {windowWidth <= 1280 && (
                    <Box
                      ml='xs'
                      flexDirection='row'
                      alignItems='center'
                      justifyContent='flex-start'>
                      <PopupProjectsFiltersButtonWeb
                        title={t('shared:filters')}
                        boxProps={{
                          position: 'left',
                          offset: [150, 0],
                        }}
                        selectedOwners={selectedOwners}
                        onSelectOwner={setSelectedOwners}
                        onSelectTeam={goToSetSeletectedTeam}
                        selectedTeamIds={selectedTeams.map((t) => t.id)}
                        onClearTeams={() => setSeletectedTeams([])}
                        selectedMembers={selectedMembers}
                        onSelectMember={setSelectedMembers}
                      />
                    </Box>
                  )}
                  {windowWidth > 1280 && (
                    <>
                      <PopupProjectButtonWeb
                        selectedOwners={selectedOwners}
                        title={t('shared:owner')}
                        boxProps={offsetPopButton}
                        onSelectOwner={setSelectedOwners}
                      />
                      <Box ml='xs'></Box>
                      <PopupProjectButtonWeb
                        title={t('shared:team')}
                        boxProps={offsetPopButton}
                        onSelectTeam={goToSetSeletectedTeam}
                        selectedTeamIds={selectedTeams.map((t) => t.id)}
                      />
                      <Box ml='xs'></Box>
                      <PopupProjectButtonWeb
                        selectedMembers={selectedMembers}
                        title={t('projects.roles.member')}
                        boxProps={offsetPopButton}
                        onSelectMember={setSelectedMembers}
                      />
                    </>
                  )}
                  <Box marginRight='xs'></Box>
                  <WebIcon
                    marginLeft='xxxs'
                    name={showGrid ? 'List' : 'Grid'}
                    color='black'
                    variant='s'
                    padding='xxs'
                    marginRight='xxxs'
                    onPress={() => setShowGrid(!showGrid)}
                  />
                  <Box width={152}>
                    <Button
                      accessibilityLabel='Create Project'
                      marginLeft='xs'
                      isSmall
                      height={getScaledSize(SCALED_DIMENSIONS.BUTTON_HEIGHT)}
                      backgroundColor='black'
                      prefix={
                        <Icon variant='s' name='FolderPlus' color='white' />
                      }
                      onPress={() => createProject()}
                      borderRadius='xs'
                      borderColor='black'
                      variant='create'
                      prefixMarginRight='xs'
                      paddingHorizontal='m'
                      paddingVertical='xs'>
                      {t('projects.create.title')}
                    </Button>
                  </Box>
                </Box>
              </Box>
              {(selectedOwners.length > 0 ||
                selectedTeams.length > 0 ||
                selectedMembers.length > 0) &&
                !isClearButtonInFirstLine && (
                  <Box
                    mt='xxxs'
                    style={{
                      flexDirection: 'row',
                      flexWrap: 'wrap',
                    }}
                    ml='none'>
                    {selectedOwners.length > 0 && !isOwnerInFirstLine && (
                      <Box
                        style={{
                          marginRight:
                            filterOwnerTagWidth < barWidth
                              ? Math.min(
                                  barWidth - filterOwnerTagWidth,
                                  spaceBetweenTags
                                )
                              : 0,
                          maxWidth: '100%',
                        }}>
                        {OwnersTag()}
                      </Box>
                    )}
                    {selectedTeams.length > 0 && !isTeamInFirstLine && (
                      <Box
                        style={{
                          marginRight:
                            filterTeamTagWidth < barWidth
                              ? Math.min(
                                  barWidth - filterTeamTagWidth,
                                  spaceBetweenTags
                                )
                              : 0,
                          maxWidth: '100%',
                        }}>
                        {TeamsTag()}
                      </Box>
                    )}
                    {selectedMembers.length > 0 && !isMemberInFirstLine && (
                      <Box
                        style={{
                          marginRight:
                            filterMemberTagWidth < barWidth
                              ? Math.min(
                                  barWidth - filterMemberTagWidth,
                                  spaceBetweenTags
                                )
                              : 0,
                          maxWidth: '100%',
                        }}>
                        {MembersTag()}
                      </Box>
                    )}
                    {ClearButton()}
                  </Box>
                )}
            </>
          ))}
        <Box mt='m' />
        {hasActiveFilters && loading && projects?.length < 1 ? (
          <SkeletonList
            row={(index) => <ProjectListRowSkeleton key={index} />}
          />
        ) : (
          <ProjectList
            alphabetize={false}
            alphabetical={false}
            projects={projects}
            expanded={showGrid}
            onClick={navigation.navigateToProject}
            loading={refreshing}
            fetchMore={fetchFromCursor}
            refreshControl={refreshControl}
            onEndReachedThreshold={1.5}
          />
        )}
      </Box>
      <ShareModal
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
        projectId={projects[0]?.uuid}
      />
      {showCreateTeamModal && (
        <Formik
          initialValues={initialValues}
          validationSchema={createTeamSchema}
          onSubmit={(values, { setSubmitting }) => {
            submitFormTeam(values, setSubmitting);
          }}>
          {() => (
            <TeamsModal
              title='Team Setup'
              buttonLabel={t('shared:next')}
              onClose={() => setShowCreateTeamModal(false)}
            />
          )}
        </Formik>
      )}
    </Box>
  );

  return (
    <Box flex={1}>
      <Helmet>
        <meta
          name='description'
          content='View and manage all your projects in one place. Organize, track progress, and collaborate seamlessly with TaskTag.'
        />
      </Helmet>
      <Box
        flex={1}
        paddingBottom={shouldShowCompleteProfileBanner ? 'm' : 'none'}>
        {AllProjectSection()}
      </Box>
      {shouldShowCompleteProfileBanner && (
        <Box
          position='absolute'
          left={theme.spacing.m}
          right={theme.spacing.m}
          bottom={theme.spacing.m}
          zIndex={1000}>
          <CompleteProfilePhoneBanner
            onPress={() => setIsEditProfileOpen(true)}
          />
        </Box>
      )}
    </Box>
  );
};
